import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, Users } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const AI_FAQS = [
  {
    id: "pf-ai-price",
    q: "What exactly do I get for £9.99?",
    a: "A complete Online AI CV Builder session — the wizard, live preview, **ATS-friendly UK CV** and a matching cover letter, downloadable as **PDF and DOCX**, plus unlimited edits from your dashboard for 6 months. Also includes Sarah, our AI Career Consultant.",
  },
  {
    id: "pf-ai-subscription",
    q: "Is there a subscription or recurring charge?",
    a: "**No.** £9.99 is a **one-off payment** via secure PayPal. No monthly fees, no auto-renewals, no hidden charges.",
  },
  {
    id: "pf-ai-preview",
    q: "Can I see my CV before I pay?",
    a: "Yes — you can preview the top portion of your AI-written CV **before you unlock**. Editing, template switching and full downloads unlock once the £9.99 payment is captured.",
  },
  {
    id: "pf-ai-credit",
    q: "What is the £9.99 credit toward Professional packages?",
    a: "If you upgrade to any Professional package (**Standard £50, Standard Package £95, Premium CV £125 or Premium Package £155**), the £9.99 you paid for the AI builder is deducted at checkout. Nothing lost either way.",
  },
  {
    id: "pf-ai-quality",
    q: "How is CV Folks AI different from other AI CV tools?",
    a: "Our AI CV Builder is developed by **professional UK CV writers** using curated CV writing methodology, recruiter-informed best practices and ATS optimisation principles — not by a generic software company. Every template follows the same standard our human writers use.",
  },
  {
    id: "pf-ai-refund",
    q: "Can I get a refund?",
    a: "Because you preview your CV **before** paying, you know exactly what you're unlocking. If there's a genuine technical issue with your generated CV, contact us and we'll make it right.",
  },
];

const PRO_FAQS = [
  {
    id: "pf-pro-price",
    q: "What do the professional packages cost?",
    a: "Four tiers: **Standard CV £50**, **Standard Package (CV + Cover Letter + LinkedIn) £95**, **Premium CV £125** (senior positioning + consultation call), and **Premium Package £155** (our flagship — everything plus a 45-min career strategy call).",
  },
  {
    id: "pf-pro-writer",
    q: "Who writes my CV?",
    a: "An **experienced UK-based CV writer** matched to your sector — NHS, IT, Finance, Marketing, HR, Engineering, Executive and more. Every CV is hand-written, never AI-generated on a Professional package.",
  },
  {
    id: "pf-pro-turnaround",
    q: "How quickly will I get my first draft?",
    a: "First draft in your inbox within **24 to 48 business hours**. Premium packages get priority 24-hour turnaround.",
  },
  {
    id: "pf-pro-guarantee",
    q: "What is the 60-day interview guarantee?",
    a: "If you don't receive an interview invitation within **60 days** of receiving your final CV, we'll rewrite it for free **and** provide extra resources and support to help you land interviews faster. Applies to every Professional package.",
  },
  {
    id: "pf-pro-revisions",
    q: "How many revisions are included?",
    a: "**Unlimited revisions** until you're completely satisfied — plus free tweaks for 6–12 months after delivery depending on your package.",
  },
  {
    id: "pf-pro-payment",
    q: "How does payment work?",
    a: "Secure one-off payment via **PayPal** (card or PayPal balance). UK company, VAT-registered, no subscription, no hidden fees.",
  },
];

const MDLink = (props) => (
  <a
    {...props}
    className="text-brand underline hover:no-underline"
    target="_blank"
    rel="noopener noreferrer"
  />
);
const MDStrong = (props) => (
  <strong {...props} className="font-semibold text-foreground" />
);
const MD_COMPONENTS = { a: MDLink, strong: MDStrong };

export default function PricingFAQ() {
  const [tab, setTab] = useState("ai");
  const items = tab === "ai" ? AI_FAQS : PRO_FAQS;

  return (
    <section
      id="pricing-faq"
      data-testid="pricing-faq-section"
      className="py-24 md:py-32 bg-muted/30"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="label-eyebrow">FAQ · Pricing</p>
          <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
            Pricing questions? We&apos;ve got you.
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground/70">
            Everything you need to know before choosing between the £9.99
            Online AI CV Builder and our Professional CV Writing packages.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div
            data-testid="pricing-faq-tabs"
            className="inline-flex items-center gap-1 bg-background border border-border rounded-full p-1"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === "ai"}
              data-testid="pricing-faq-tab-ai"
              onClick={() => setTab("ai")}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                tab === "ai"
                  ? "bg-foreground text-background shadow"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              <Sparkles className="h-4 w-4" />
              AI CV Builder · £9.99
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "pro"}
              data-testid="pricing-faq-tab-pro"
              onClick={() => setTab("pro")}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                tab === "pro"
                  ? "bg-foreground text-background shadow"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              <Users className="h-4 w-4" />
              Professional · from £50
            </button>
          </div>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {items.map((item) => (
            <AccordionItem
              key={item.id}
              value={item.id}
              data-testid={`pricing-faq-item-${item.id}`}
              className="rounded-2xl border border-border bg-card px-5 md:px-6"
            >
              <AccordionTrigger
                data-testid={`pricing-faq-trigger-${item.id}`}
                className="text-left font-serif text-lg md:text-xl font-semibold hover:no-underline py-5"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-foreground/80 leading-relaxed pb-6">
                <div className="faq-answer space-y-3">
                  <ReactMarkdown components={MD_COMPONENTS}>
                    {item.a || ""}
                  </ReactMarkdown>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
